import type { RestrictionEligibilite, TypeAbsence } from '../services/conges-api';

/**
 * Ce que le formulaire d'un type d'absence envoie au serveur.
 *
 * VIT DANS SON PROPRE FICHIER pour être LU par `scripts/test-conges-contrat.ts`,
 * qui vérifie que chaque champ envoyé existe dans le schéma Zod du serveur.
 *
 * Le 13/08/2026, `plafond_annuel` et `aide_plafond` ont été ajoutés à la base, à
 * l'écran et à l'amorçage — mais pas au schéma d'entrée, qui refuse toute clé
 * inconnue. Plus aucun type ne pouvait être enregistré, et le message rendu à
 * l'utilisateur parlait de « raison technique ». Le module Notes de frais avait
 * déjà ce test ; les congés ne l'avaient pas.
 */

/** L'état du formulaire : les mécanismes y sont un TABLEAU, pas du JSON. */
export interface BrouillonType {
  id?: string;
  libelle: string;
  categorie_id: string;
  origine: 'collaborateur' | 'rh';
  visible_collaborateur: boolean;
  restriction_eligibilite: RestrictionEligibilite;
  mecanismes: string[];
  plafond_annuel: number | null;
  plafond_periode: 'an' | 'evenement';
  aide_plafond: string;
  bareme_intro: string;
  code_silae: string;
  ordre: number;
  actif: boolean;
}

/** Le mécanisme qui porte le plafond et son texte d'aide. */
export const MECANISME_PLAFOND = 'compteur_annuel_simple';

/** Le mécanisme qui porte le barème et sa phrase d'introduction. */
export const MECANISME_BAREME = 'bareme_aide';

/**
 * Construit le corps de la requête.
 *
 * Quand le mécanisme du plafond n'est pas activé, plafond et texte d'aide
 * partent à `null` : sans cela, une valeur saisie puis masquée continuerait
 * d'exister en base sans que personne ne la voie — et le « ? » l'afficherait au
 * salarié le jour où le mécanisme serait réactivé.
 */
export function corpsTypeAbsence(b: BrouillonType) {
  const avecPlafond = b.mecanismes.includes(MECANISME_PLAFOND);
  return {
    libelle: b.libelle,
    categorie_id: b.categorie_id,
    origine: b.origine,
    visible_collaborateur: b.visible_collaborateur,
    restriction_eligibilite: b.restriction_eligibilite,
    mecanismes: b.mecanismes,
    plafond_annuel: avecPlafond ? b.plafond_annuel : null,
    plafond_periode: avecPlafond ? b.plafond_periode : 'an',
    aide_plafond: avecPlafond ? b.aide_plafond || null : null,
    // Même règle que le texte d'aide : sans le mécanisme, la phrase n'a plus
    // rien à introduire et ne doit pas survivre en base.
    bareme_intro: b.mecanismes.includes(MECANISME_BAREME) ? b.bareme_intro || null : null,
    code_silae: b.code_silae || null,
    ordre: b.ordre,
    actif: b.actif,
  };
}

export function brouillonVide(categorieId: string, ordre: number): BrouillonType {
  return {
    libelle: '',
    categorie_id: categorieId,
    // Un type créé se saisit côté RH par défaut : l'ouvrir au collaborateur est
    // une décision, pas un oubli.
    origine: 'rh',
    visible_collaborateur: false,
    restriction_eligibilite: 'aucune',
    mecanismes: [],
    plafond_annuel: null,
    plafond_periode: 'an',
    aide_plafond: '',
    bareme_intro: '',
    code_silae: '',
    ordre,
    actif: true,
  };
}

export function brouillonDe(t: TypeAbsence): BrouillonType {
  return {
    id: t.id,
    libelle: t.libelle,
    categorie_id: t.categorie_id ?? '',
    origine: t.origine,
    visible_collaborateur: t.visible_collaborateur,
    restriction_eligibilite: t.restriction_eligibilite,
    mecanismes: lireMecanismes(t.mecanismes),
    plafond_annuel: t.plafond_annuel,
    plafond_periode: t.plafond_periode,
    aide_plafond: t.aide_plafond ?? '',
    bareme_intro: t.bareme_intro ?? '',
    code_silae: t.code_silae ?? '',
    ordre: t.ordre,
    actif: t.actif,
  };
}

/** Lit la colonne JSON sans jamais lever : une valeur illisible vaut « aucun ». */
export function lireMecanismes(brut: string | null | undefined): string[] {
  if (!brut) return [];
  try {
    const v = JSON.parse(brut);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Le plafond, dit comme le salarié doit le comprendre.
 *
 * « 3 jours par an » pour un enfant malade, « 3 jours par événement » pour une
 * naissance — deux naissances dans l'année ouvrent deux fois le droit. Le
 * compteur, lui, reste annuel : il alerte le valideur, qui tranche. Afficher
 * « par an » dans ce cas ferait croire au salarié qu'il a épuisé un droit qu'il
 * n'a pas épuisé.
 *
 * Rend `null` quand il n'y a rien à dire : ni plafond, ni mécanisme pour le
 * porter.
 */
export function libellePlafond(t: {
  mecanismes: string | null;
  plafond_annuel: number | null;
  plafond_periode: string;
}): string | null {
  if (!t.plafond_annuel || !lireMecanismes(t.mecanismes).includes(MECANISME_PLAFOND)) return null;
  const jours = `${t.plafond_annuel} jour${t.plafond_annuel > 1 ? 's' : ''}`;
  return t.plafond_periode === 'evenement' ? `${jours} par événement` : `${jours} par an`;
}
